import { useEffect, useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AdminListPage } from '../components/AdminListPage'
import { ReviewInvoiceDrawer } from '../components/invoices/ReviewInvoiceDrawer'
import { useAdminInvoices, type AdminInvoiceRow } from '../hooks/useAdminInvoices'
import { typedCollection, getCollection } from '@/lib/firebase/firestore'
import type { CompanyMemberDocument, ProviderInvoiceDocument, ProviderInvoiceStatus } from '@/types'

const STATUS_BADGE: Record<
  ProviderInvoiceStatus,
  { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  draft: { label: 'Draft', variant: 'secondary' },
  submitted: { label: 'Submitted', variant: 'outline' },
  approved: { label: 'Approved', variant: 'default' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  paid: { label: 'Paid', variant: 'default' },
}

const TABS: Array<{ value: ProviderInvoiceStatus | 'all'; label: string }> = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
  { value: 'all', label: 'All' },
]

function formatSubmittedAt(ts: AdminInvoiceRow['submittedAt']): string {
  if (!ts) return '—'
  const d = ts.toDate()
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function ContractorInvoicesPage() {
  const {
    invoices,
    counts,
    loading,
    error,
    filterStatus,
    setFilterStatus,
    refresh,
    approve,
    reject,
    markPaid,
    editLineItems,
  } = useAdminInvoices()

  const [companyNames, setCompanyNames] = useState<Map<string, string>>(new Map())
  const [search, setSearch] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selected, setSelected] = useState<AdminInvoiceRow | null>(null)

  // Batch-load company name map once (small collection, avoids per-row reads).
  useEffect(() => {
    let cancelled = false
    getCollection(typedCollection<CompanyMemberDocument>('company'))
      .then((rows) => {
        if (cancelled) return
        const m = new Map<string, string>()
        for (const r of rows) {
          m.set(r.id, r.name)
          if (r.linkedUserId) m.set(r.linkedUserId, r.name)
        }
        setCompanyNames(m)
      })
      .catch(() => {
        /* non-fatal — fall back to memberId display */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const providerNameFor = (invoice: ProviderInvoiceDocument): string =>
    companyNames.get(invoice.providerMemberId ?? '') ??
    companyNames.get(invoice.memberId) ??
    invoice.providerMemberId ??
    invoice.memberId

  const filtered = useMemo(() => {
    if (!search.trim()) return invoices
    const term = search.trim().toLowerCase()
    return invoices.filter(
      (inv) =>
        inv.productionName.toLowerCase().includes(term) ||
        providerNameFor(inv).toLowerCase().includes(term),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, search, companyNames])

  function openRow(row: AdminInvoiceRow) {
    setSelected(row)
    setDrawerOpen(true)
  }

  return (
    <AdminListPage
        title="Provider Invoices"
        filters={
          <div className="space-y-4">
            <Tabs
              value={filterStatus}
              onValueChange={(v) => setFilterStatus(v as ProviderInvoiceStatus | 'all')}
            >
              <TabsList>
                {TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label} ({counts[t.value] ?? 0})
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap gap-2">
              <Input
                placeholder="Search provider or production…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-72"
              />
            </div>
          </div>
        }
        error={error}
        loading={loading}
        dialog={
          <ReviewInvoiceDrawer
            open={drawerOpen}
            onOpenChange={(o) => {
              setDrawerOpen(o)
              if (!o) setSelected(null)
            }}
            invoice={selected}
            providerName={selected ? providerNameFor(selected) : ''}
            onActionComplete={refresh}
            onApprove={approve}
            onReject={reject}
            onMarkPaid={markPaid}
            onEditLineItems={editLineItems}
          />
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Production</TableHead>
              <TableHead>Subtotal</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No invoices in this view.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((inv) => {
                const badge = STATUS_BADGE[inv.status]
                return (
                  <TableRow
                    key={inv.id}
                    className="cursor-pointer"
                    onClick={() => openRow(inv)}
                  >
                    <TableCell className="font-medium">
                      {providerNameFor(inv)}
                    </TableCell>
                    <TableCell>{inv.productionName}</TableCell>
                    <TableCell>${inv.subtotal.toLocaleString()}</TableCell>
                    <TableCell>{formatSubmittedAt(inv.submittedAt)}</TableCell>
                    <TableCell>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
    </AdminListPage>
  )
}
